import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Button from '../components/ui/Button';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>

        <div className="prose prose-slate max-w-none space-y-6">
          <section>
            <h2 className="text-2xl font-semibold mb-3">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground">
              By accessing and using Earn My Edge ("the Service"), you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">2. Description of Service</h2>
            <p className="text-muted-foreground">
              Earn My Edge is an AI-powered sales training platform that provides daily drills, coaching feedback, 
              objection handling practice, and performance tracking tools to help sales professionals improve their skills.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">3. User Accounts</h2>
            <div className="space-y-3">
              <p className="text-muted-foreground">
                <strong>3.1 Registration:</strong> You must create an account to use certain features of the Service. 
                You agree to provide accurate, current, and complete information during registration.
              </p>
              <p className="text-muted-foreground">
                <strong>3.2 Account Security:</strong> You are responsible for maintaining the confidentiality of your 
                account credentials and for all activities that occur under your account.
              </p>
              <p className="text-muted-foreground">
                <strong>3.3 Account Sharing:</strong> Accounts are for individual use only. We enforce a maximum of 2 
                concurrent active sessions per account. Sharing accounts or attempting to circumvent session limits may 
                result in account suspension or termination.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">4. Subscription Plans and Billing</h2>
            <div className="space-y-3">
              <p className="text-muted-foreground">
                <strong>4.1 Subscription:</strong> Access is provided through a single paid Elite plan available on monthly or annual billing. There is no free tier.
              </p>
              <p className="text-muted-foreground">
                <strong>4.2 Pricing:</strong> Current pricing is displayed on our pricing page and may change with 30 days' notice.
              </p>
              <p className="text-muted-foreground">
                <strong>4.3 Payment Processing:</strong> Payments are processed through our third-party payment provider, 
                Whop. By subscribing, you agree to their terms of service.
              </p>
              <p className="text-muted-foreground">
                <strong>4.4 Cancellation:</strong> You may cancel your subscription at any time. Cancellation will take 
                effect at the end of your current billing period. No refunds are provided for partial months.
              </p>
              <p className="text-muted-foreground">
                <strong>4.5 Refunds:</strong> Refunds are provided at our sole discretion within 7 days of initial purchase 
                for paid plans if you have not substantially used the Service.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">5. Acceptable Use</h2>
            <div className="space-y-3">
              <p className="text-muted-foreground">You agree not to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Attempt to gain unauthorized access to any portion of the Service, including backend systems, APIs, or databases</li>
                <li>Reverse engineer, decompile, disassemble, or otherwise attempt to discover the source code, algorithms, or proprietary methods of the Service</li>
                <li>Copy, reproduce, modify, or create derivative works based on any aspect of the Service, including AI models, training content, user interface, workflows, or business processes</li>
                <li>Use automated tools, bots, scrapers, or scripts to access, monitor, copy, or extract data from the Service without explicit written permission</li>
                <li>Record, screenshot, or capture the Service's interface, features, or content for the purpose of competitive analysis or replication</li>
                <li>Share, distribute, or publish any proprietary information, methodologies, training materials, or AI-generated content obtained through the Service</li>
                <li>Share your account credentials, sell access to your account, or allow unauthorized third parties to access the Service through your account</li>
                <li>Benchmark the Service against competing products or services for commercial purposes</li>
                <li>Use the Service to develop, train, or improve competing AI models or sales coaching platforms</li>
                <li>Upload malicious code, viruses, or attempt to disrupt, damage, or impair the Service</li>
                <li>Violate any applicable laws, regulations, or third-party rights</li>
                <li>Engage in any activity that could harm Earn My Edge's reputation or business interests</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                <strong>Enforcement:</strong> Violation of these acceptable use provisions may result in immediate account suspension or termination without refund, legal action for damages, and pursuit of injunctive relief to prevent further harm.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">6. Intellectual Property</h2>
            <div className="space-y-3">
              <p className="text-muted-foreground">
                <strong>6.1 Ownership:</strong> All content, features, and functionality of the Service, including but 
                not limited to AI models, training algorithms, coaching methodologies, drill content, feedback systems, 
                user interface designs, software architecture, business processes, text, graphics, logos, and code, are 
                owned by Earn My Edge and protected by United States and international copyright, trademark, patent, 
                trade secret, and other intellectual property laws.
              </p>
              <p className="text-muted-foreground">
                <strong>6.2 Proprietary Technology:</strong> The Service incorporates proprietary artificial intelligence 
                models, natural language processing systems, scoring algorithms, and adaptive learning technologies that 
                represent significant investment and trade secrets. Any attempt to extract, reverse engineer, or replicate 
                these systems is strictly prohibited and will be prosecuted to the fullest extent of the law.
              </p>
              <p className="text-muted-foreground">
                <strong>6.3 Limited License:</strong> We grant you a limited, non-exclusive, non-transferable, revocable 
                license to access and use the Service solely for your personal or internal business training purposes. 
                This license does not grant you any rights to: (a) use the Service to develop competing products, (b) 
                extract or analyze the Service's methodologies for commercial purposes, (c) train AI models using our 
                content or feedback, or (d) incorporate any aspect of the Service into other products or services.
              </p>
              <p className="text-muted-foreground">
                <strong>6.4 User Content:</strong> You retain ownership of practice responses, recordings, personal 
                notes, uploaded scripts, training materials, and other content you submit to the Service ("User Content"). 
                By submitting User Content, you grant us a worldwide, non-exclusive, royalty-free, perpetual license to 
                use, store, process, analyze, reproduce, modify, and create derivative works from it for the purposes of: 
                (a) providing and improving the Service, (b) training and refining our AI models, (c) developing new 
                features and training content, and (d) creating anonymized or aggregated data for research and analytics.
              </p>
              <p className="text-muted-foreground">
                <strong>6.4.1 User Warranties:</strong> By uploading content, you represent and warrant that: (a) you own 
                or have obtained all necessary rights, licenses, consents, and permissions to upload and grant us the 
                licenses described above, (b) your uploaded content does not infringe any third-party intellectual property 
                rights or violate any confidentiality obligations, (c) if you are uploading employer-provided materials, 
                you have express authorization from your employer to do so, and (d) for team accounts, you have authority 
                to upload materials on behalf of your organization.
              </p>
              <p className="text-muted-foreground">
                <strong>6.4.2 Indemnification for Uploads:</strong> You agree to indemnify and hold harmless Earn My Edge 
                from any claims, damages, losses, or expenses (including attorneys' fees) arising from: (a) your 
                unauthorized upload of third-party materials, (b) breach of confidentiality obligations to your employer 
                or other parties, (c) infringement of intellectual property rights through uploaded content, or (d) 
                misrepresentation of your authority to upload materials.
              </p>
              <p className="text-muted-foreground">
                <strong>6.4.3 Data Privacy:</strong> We will not use your User Content to train competing AI models or 
                share it with third parties except as necessary to provide the Service. Uploaded training materials from 
                team accounts will remain confidential to that team and will not be shown to other users unless anonymized 
                and aggregated for general training purposes.
              </p>
              <p className="text-muted-foreground">
                <strong>6.5 Confidential Information:</strong> By using the Service, you acknowledge that you may be 
                exposed to confidential and proprietary information about our AI systems, training methodologies, and 
                business operations. You agree to treat all such information as confidential and not disclose it to any 
                third party or use it for any purpose other than your authorized use of the Service.
              </p>
              <p className="text-muted-foreground">
                <strong>6.6 No Transfer of Rights:</strong> Nothing in these Terms transfers any ownership rights in the 
                Service or its intellectual property to you. All rights not expressly granted are reserved by Earn My Edge.
              </p>
              <p className="text-muted-foreground">
                <strong>6.7 DMCA and Infringement:</strong> We take intellectual property rights seriously. If you believe 
                someone is using the Service to infringe your intellectual property, or if we believe you are infringing 
                ours, we will take appropriate action including account termination and legal proceedings.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">7. Prohibition on Competitive Intelligence and Analysis</h2>
            <div className="space-y-3">
              <p className="text-muted-foreground">
                <strong>7.1 No Competitive Use:</strong> Users affiliated with competing sales training platforms, AI coaching services, or sales enablement tools may not access the Service for the purpose of competitive analysis, benchmarking, or feature comparison. Creating an account under false pretenses for competitive intelligence purposes is strictly prohibited.
              </p>
              <p className="text-muted-foreground">
                <strong>7.2 Monitoring and Detection:</strong> Earn My Edge actively monitors for suspicious activity patterns including but not limited to: systematic feature exploration, unusual data export attempts, rapid navigation through training content without engagement, coordinated access from multiple accounts, and behavior consistent with competitive reconnaissance. We reserve the right to request verification of legitimate use.
              </p>
              <p className="text-muted-foreground">
                <strong>7.3 Competitor Restrictions:</strong> If you are developing, operating, or employed by a competing service, you must disclose this relationship before creating an account. We reserve the right to deny access to competitors or require additional agreements governing your use of the Service.
              </p>
              <p className="text-muted-foreground">
                <strong>7.4 Non-Disclosure Obligation:</strong> Users agree not to disclose, publish, or share any information about the Service's features, capabilities, methodologies, user experience, or implementation details with third parties, including competitors, investors, or media, without prior written consent from Earn My Edge.
              </p>
              <p className="text-muted-foreground">
                <strong>7.5 Consequences:</strong> Violation of this section will result in immediate account termination without refund, pursuit of injunctive relief to prevent further access or use of obtained information, and legal action to recover damages including but not limited to attorneys' fees, lost business opportunities, and compensation for trade secret misappropriation.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">8. AI-Generated Content</h2>
            <p className="text-muted-foreground mb-2">
              Our Service uses artificial intelligence to provide personalized feedback, coaching insights, and training recommendations. This AI-generated content is proprietary and subject to the intellectual property protections in Section 6.
            </p>
            <p className="text-muted-foreground mb-2">
              While we strive for accuracy, AI-generated content may occasionally contain errors or suggestions that do not fit your specific situation. You are responsible for evaluating and applying AI-generated advice appropriately.
            </p>
            <p className="text-muted-foreground">
              You may not use AI-generated content provided by the Service to: (a) train competing AI models, (b) create datasets for machine learning applications, (c) reverse engineer our AI systems, or (d) redistribute or commercialize the content outside the Service. All AI-generated content remains the intellectual property of Earn My Edge.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">9. Data and Privacy</h2>
            <p className="text-muted-foreground mb-2">
              Your use of the Service is subject to our Privacy Policy, which describes how we collect, use, and protect 
              your personal information. By using the Service, you consent to our data practices as described in the 
              Privacy Policy.
            </p>
            <p className="text-muted-foreground">
              <strong>Data Security and Confidentiality:</strong> You acknowledge that through use of the Service, you may observe or gain access to information about our training methodologies, AI prompts, coaching frameworks, and business processes. This information is confidential and proprietary. You agree not to use this information outside the Service or share it with third parties for any purpose.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">10. Disclaimers and Limitation of Liability</h2>
            <div className="space-y-3">
              <p className="text-muted-foreground">
                <strong>9.1 "As Is" Service:</strong> The Service is provided "as is" and "as available" without warranties 
                of any kind, either express or implied.
              </p>
              <p className="text-muted-foreground">
                <strong>9.2 No Guarantee:</strong> We do not guarantee that the Service will be uninterrupted, secure, or 
                error-free, or that any content will be accurate or reliable.
              </p>
              <p className="text-muted-foreground">
                <strong>9.3 Limitation:</strong> To the maximum extent permitted by law, Earn My Edge shall not be liable 
                for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">10. Termination</h2>
            <p className="text-muted-foreground">
              We reserve the right to suspend or terminate your account at any time for violation of these Terms or for 
              any other reason at our sole discretion. Upon termination, your right to use the Service will immediately cease.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">11. Changes to Terms</h2>
            <p className="text-muted-foreground">
              We reserve the right to modify these Terms at any time. We will notify users of material changes via email 
              or through the Service. Your continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">12. Governing Law</h2>
            <p className="text-muted-foreground">
              These Terms shall be governed by and construed in accordance with the laws of the United States, without 
              regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">13. Affiliate Program Terms</h2>
            <div className="space-y-3">
              <p className="text-muted-foreground">
                <strong>13.1 Affiliate Relationship:</strong> If you participate in the Earn My Edge affiliate program, you agree to promote the Service in accordance with these additional terms. The affiliate relationship is non-exclusive and may be terminated by either party at any time.
              </p>
              <p className="text-muted-foreground">
                <strong>13.2 Non-Circumvention:</strong> Affiliates may not bypass Earn My Edge to directly engage, contract with, or sell services to customers referred through their affiliate link. Affiliates may not create competing AI sales coaching services using knowledge, insights, or information gained through the affiliate partnership. This restriction applies during the partnership and for 12 months following termination.
              </p>
              <p className="text-muted-foreground">
                <strong>13.3 Intellectual Property Protection:</strong> All platform technology, AI models, training content, branding, and proprietary methodologies remain the exclusive property of Earn My Edge. Affiliates may not reverse engineer, copy, replicate, or create derivative works based on any aspect of the Service. Violation of this clause results in immediate termination and forfeiture of all unpaid commissions.
              </p>
              <p className="text-muted-foreground">
                <strong>13.4 Customer Ownership:</strong> All customers referred via an affiliate link remain Earn My Edge customers. Affiliates may not poach, recruit, solicit, or redirect referred customers to competing services or platforms. Affiliates may not contact referred customers for purposes other than legitimate support of their use of Earn My Edge. Violation results in commission forfeiture and potential legal action for damages.
              </p>
              <p className="text-muted-foreground">
                <strong>13.5 Confidentiality:</strong> Affiliates agree to maintain strict confidentiality regarding all business information obtained through the affiliate partnership, including but not limited to: pricing structures, commission rates, product roadmaps, technical architecture, customer data, business strategies, and internal operations. This confidentiality obligation survives termination indefinitely. Affiliates may not share confidential information with third parties or use it for personal benefit or competitive purposes.
              </p>
              <p className="text-muted-foreground">
                <strong>13.6 Commission Structure:</strong> Affiliates earn recurring commissions on active subscriptions generated through their unique referral link. Commission rates and payment terms are subject to change with 30 days' notice. Commissions are forfeited if the affiliate violates any terms of this agreement or engages in fraudulent activity.
              </p>
              <p className="text-muted-foreground">
                <strong>13.7 Prohibited Activities:</strong> Affiliates may not: (a) engage in trademark or brand bidding on search engines, (b) use spam, misleading advertising, or false claims, (c) make unauthorized representations about the Service, (d) create confusion about their relationship with Earn My Edge, or (e) promote the Service through any illegal or unethical means.
              </p>
              <p className="text-muted-foreground">
                <strong>13.8 Termination Rights:</strong> Earn My Edge reserves the right to immediately terminate an affiliate partnership if the affiliate: (a) competes directly with Earn My Edge, (b) violates any provision of this agreement, (c) engages in conduct that damages Earn My Edge's reputation, or (d) fails to maintain professional standards. Upon termination for cause, all unpaid commissions are forfeited and the affiliate must immediately cease all promotional activities.
              </p>
              <p className="text-muted-foreground">
                <strong>13.9 Independent Contractor:</strong> Affiliates are independent contractors, not employees, partners, or agents of Earn My Edge. Affiliates have no authority to bind Earn My Edge or make commitments on its behalf. How affiliates structure payment arrangements with their own teams or customers is solely their responsibility and independent of their relationship with Earn My Edge.
              </p>
              <p className="text-muted-foreground">
                <strong>13.10 Indemnification:</strong> Affiliates agree to indemnify and hold harmless Earn My Edge from any claims, damages, or expenses arising from their promotional activities, breach of these terms, or violation of any laws or third-party rights.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">14. Uploaded Training Materials</h2>
            <div className="space-y-3">
              <p className="text-muted-foreground">
                <strong>14.1 Upload Feature:</strong> The Service allows users to upload proprietary sales scripts, 
                training materials, objection handling guides, and other content to customize their training experience. 
                This feature is designed to make coaching more relevant to your specific sales methodology and product offerings.
              </p>
              <p className="text-muted-foreground">
                <strong>14.2 AI Training Consent:</strong> By uploading training materials, you explicitly consent to 
                Earn My Edge using these materials to: (a) train, improve, and fine-tune our AI coaching models, (b) 
                develop new training content, drills, and objection scenarios, (c) enhance our natural language processing 
                and feedback systems, and (d) create derivative training materials based on patterns identified across 
                multiple uploads. This consent is irrevocable for materials already used in AI training, though you may 
                request deletion of the original uploaded files.
              </p>
              <p className="text-muted-foreground">
                <strong>14.3 Review and Approval:</strong> Earn My Edge reserves the right to review all uploaded 
                materials before incorporating them into AI training pipelines. We may reject, remove, or flag uploads 
                that: (a) contain sensitive personal information, (b) include competitor trademarks or confidential 
                information belonging to third parties, (c) violate intellectual property rights, (d) contain illegal, 
                harmful, or inappropriate content, or (e) appear to be uploaded without proper authorization.
              </p>
              <p className="text-muted-foreground">
                <strong>14.4 Team Account Authorization:</strong> For team accounts, only users with "Owner" or "Manager" 
                roles may upload training materials. Uploaded materials are associated with the team (not individual users) 
                and remain accessible to all team members with appropriate permissions. Team administrators are responsible 
                for ensuring all uploads comply with organizational policies and legal requirements.
              </p>
              <p className="text-muted-foreground">
                <strong>14.5 Data Segregation:</strong> While we may use your uploaded materials to improve our platform 
                generally, we will not directly show your proprietary scripts, methodologies, or training content to other 
                users or teams unless: (a) you explicitly authorize public sharing, (b) the content is anonymized and 
                aggregated beyond recognition, or (c) necessary to comply with legal obligations.
              </p>
              <p className="text-muted-foreground">
                <strong>14.6 Deletion Requests:</strong> You may request deletion of uploaded files at any time through 
                your account settings or by contacting support. However, deletion of the original file does not remove: 
                (a) derivative works, patterns, or insights already extracted from the content, (b) AI model weights or 
                training data derived from the upload, (c) anonymized or aggregated data incorporating elements from your 
                upload, or (d) backup copies retained for legal or operational purposes for up to 90 days.
              </p>
              <p className="text-muted-foreground">
                <strong>14.7 No Compensation:</strong> You will not receive any compensation, royalties, or attribution 
                for uploaded materials used to improve the Service, regardless of the value derived from such materials. 
                The benefit you receive is enhanced, personalized training aligned with your specific methodology.
              </p>
              <p className="text-muted-foreground">
                <strong>14.8 Competitive Use:</strong> Uploaded training materials will not be used to develop competing 
                products outside the Earn My Edge platform or sold/licensed to third-party competitors. However, insights, 
                patterns, and methodologies learned from your uploads may inform general platform improvements that benefit 
                all users.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">15. Contact Information</h2>
            <p className="text-muted-foreground">
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <p className="text-muted-foreground font-medium">
              Email: support@earnmyedge.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
